/**
 * NotEnabledModal — shown when user tries to edit/view/change-status on a record
 * that is NOT yet enabled (assigned) for the active company context.
 *
 * Styles match corporate mock patterns:
 *   .modal-overlay  → fixed inset-0, rgba(15,23,42,.45), backdrop-filter:blur(2px)
 *   .modal          → borderRadius:12, boxShadow double-layer, width:420
 *   .modal-header   → padding:18px 20px 14px, border-bottom, icon-wrap.blue
 *   .modal-body     → padding:16px 20px, gap:12px
 *   .modal-footer   → padding:12px 20px, bg:#f4f4f5, border-top, btn-secondary + btn-primary
 *
 * Trigger determines body text and confirm button label:
 *   OPEN_EDIT     → editing flow
 *   OPEN_VIEW     → view-only flow
 *   CHANGE_STATUS → status toggle flow
 *
 * @module components/modals/NotEnabledModal
 * @see FMPV-MVWC-E004-S005
 */
import React from 'react';
import { Building2, AlertTriangle, X } from 'lucide-react';
import { Button } from '../../../Button/Button';

export type NotEnabledModalTrigger = 'OPEN_EDIT' | 'CHANGE_STATUS' | 'OPEN_VIEW' | 'COMPANY_SWITCH';

export interface NotEnabledModalProps {
  isOpen: boolean;
  trigger: NotEnabledModalTrigger;
  /** Display name of the active company (shown in header subtitle and body) */
  companyName: string;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function NotEnabledModal({
  isOpen,
  trigger,
  companyName,
  onCancel,
  onConfirm,
  isLoading,
  t,
}: NotEnabledModalProps): React.ReactElement | null {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const getActionText = (trig: NotEnabledModalTrigger): string => {
    switch (trig) {
      case 'OPEN_EDIT': return tt('notEnabledActionEdit', '¿Desea habilitarlo y continuar con la edición?');
      case 'CHANGE_STATUS': return tt('notEnabledActionChangeStatus', '¿Desea habilitarlo y cambiar su estado?');
      case 'OPEN_VIEW': return tt('notEnabledActionView', '¿Desea habilitarlo y continuar con la visualización?');
      case 'COMPANY_SWITCH': return tt('notEnabledActionCompanySwitch', '¿Desea habilitarlo para continuar con la edición en esta compañía?');
    }
  };
  const getConfirmLabel = (trig: NotEnabledModalTrigger): string => {
    switch (trig) {
      case 'CHANGE_STATUS': return tt('notEnabledConfirmChangeStatus', 'Habilitar y cambiar estado');
      default: return tt('notEnabledConfirmContinue', 'Habilitar y continuar');
    }
  };
  if (!isOpen) return null;

  return (
    /* Overlay — matches mock .modal-overlay */
    <div
      className="fixed inset-0 flex items-center justify-center z-[9000]"
      style={{ background: 'rgba(15,23,42,.45)', backdropFilter: 'blur(2px)' }}
      role="dialog"
      aria-modal="true"
    >
      {/* Modal — matches mock .modal */}
      <div
        data-testid="not-enabled-modal"
        className="bg-bg-primary dark:bg-dark-bg-secondary border border-transparent dark:border-dark-border-primary flex flex-col overflow-hidden"
        style={{
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.22),0 4px 16px rgba(0,0,0,.1)',
          maxHeight: '78vh',
          width: 420,
        }}
      >
        {/* Header — matches mock .modal-header */}
        <div
          className="flex items-start justify-between border-b border-border-primary dark:border-dark-border-primary gap-3"
          style={{ padding: '18px 20px 14px' }}
        >
          {/* Left: icon + title / subtitle */}
          <div className="flex items-start gap-3">
            {/* Icon wrap blue — matches mock .modal-icon-wrap.blue */}
            <div
              className="flex-shrink-0 flex items-center justify-center bg-[#eff8ff] dark:bg-[rgba(14,121,253,0.15)] text-[#0e79fd] dark:text-[#60b6fa]"
              style={{ width: 36, height: 36, borderRadius: 8 }}
            >
              <Building2 size={18} />
            </div>
            <div>
              {/* .modal-title */}
              <div className="font-bold text-content-primary dark:text-dark-content-primary leading-snug" style={{ fontSize: 14 }}>
                {tt('notEnabledTitle', 'Registro no habilitado')}
              </div>
              {/* .modal-desc */}
              <div className="text-content-tertiary dark:text-dark-content-tertiary leading-relaxed" style={{ fontSize: 12, marginTop: 3 }}>
                {companyName}
              </div>
            </div>
          </div>

          {/* Close button — matches mock .modal-close */}
          <button
            type="button"
            data-testid="not-enabled-close-button"
            onClick={onCancel}
            className="flex-shrink-0 flex items-center justify-center p-1 rounded-md border-0 bg-transparent text-content-secondary dark:text-dark-content-secondary cursor-pointer hover:bg-background-secondary dark:hover:bg-dark-bg-secondary hover:text-content-tertiary dark:hover:text-dark-content-tertiary transition-colors leading-none"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — matches mock .modal-body */}
        <div
          className="flex-1 overflow-y-auto flex flex-col"
          style={{ padding: '16px 20px', gap: 12 }}
        >
          <p
            data-testid="not-enabled-body"
            className="text-content-primary dark:text-dark-content-primary" style={{ fontSize: 13, lineHeight: 1.6 }}
          >
            {tt('notEnabledBodyPrefix', 'Este registro no está habilitado en ')}<strong>{companyName}</strong>.{' '}
            {getActionText(trigger)}
          </p>

          {/* Yellow warning callout — matches mock's inline amber callout */}
          <div
            className="flex items-start gap-2 bg-[#fffdf0] dark:bg-[rgba(251,191,36,0.08)]"
            style={{
              borderLeft: '3px solid #fbbf24',
              borderRadius: '0 4px 4px 0',
              padding: '5px 10px',
            }}
          >
            <AlertTriangle size={12} className="flex-shrink-0 text-[#d97706] dark:text-[#fbbf24]" style={{ marginTop: 2 }} />
            <span className="text-[#92400e] dark:text-[#fcd34d]" style={{ fontSize: 11 }}>
              {tt('notEnabledWarning', 'Los valores globales se conservarán hasta que se personalicen los campos.')}
            </span>
          </div>
        </div>

        {/* Footer — matches mock .modal-footer (bg:#f4f4f5) */}
        <div
          className="flex items-center justify-end gap-2 border-t border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-primary"
          style={{ padding: '12px 20px' }}
        >
          <Button
            data-testid="not-enabled-cancel-button"
            type="outline"
            size="base"
            className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap"
            onClick={onCancel}
            disabled={isLoading}
          >
            {tt('cancel', 'Cancelar')}
          </Button>
          <Button
            data-testid="not-enabled-confirm-button"
            type="default"
            size="base"
            className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? tt('notEnabledLoadingLabel', 'Habilitando…') : getConfirmLabel(trigger)}
          </Button>
        </div>
      </div>
    </div>
  );
}
